import ErrorBoundary from 'components/error-boundaries';
import PageTemplate from 'modules/bot-monitor-shared/components/page-template';
import Header from 'modules/marketplace-performance-page/components/header';
import MainPerformance from 'modules/marketplace-performance-page/components/main-performance';
import Stats from 'modules/marketplace-performance-page/components/stats';
import { useHeaderInfo } from 'modules/marketplace-performance-page/query-hooks/use-header-info';
import { useParams } from 'react-router-dom';
import { FullPageNotification } from 'modules/bots-performance/components/full-page-notification';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { HeaderLoading } from 'modules/marketplace-performance-page/components/header-loading';
import { useStatsInfo } from 'modules/marketplace-performance-page/query-hooks/use-stats-info';
import { StatsLoading } from 'modules/marketplace-performance-page/components/stats-loading';
import mizarLogo from 'assets/img/logo/mizar_icon.svg';

const MarketplacePerformancePage = () => {
  const { botId } = useParams<{ botId: string }>();
  const { headerInfoStats, headerInfoStatsError, isLoadingHeaderInfoStats } =
    useHeaderInfo({
      botId,
    });
  const { stats, statsError, isLoadingStats } = useStatsInfo({
    botId,
  });

  const isError = headerInfoStatsError || statsError;

  if (isError) {
    const isStatsCalculating =
      statsError && statsError === 'Evaluating strategy performance...';

    return (
      <PageTemplate
        pages={[
          { name: 'Marketplace', href: '/dashboard/marketplace' },
          { name: '--', href: null },
        ]}
      >
        <FullPageNotification
          height="85vh"
          icon={
            isStatsCalculating ? (
              <img alt="mizar logo light" src={mizarLogo} height="100" />
            ) : (
              <XCircleIcon
                className="flex-shrink-0 h-28 w-28 align-center text-red-warning"
                aria-hidden="true"
              />
            )
          }
          title={
            isStatsCalculating
              ? 'Mizar is calculating your stats.'
              : 'Oops, something has gone wrong here'
          }
          subTitle={
            isStatsCalculating
              ? 'We are crunching the numbers to calculate your stats. This process may take a few minutes, so please hang tight!'
              : 'There was an error while loading this page. Try to reload it again in 30 seconds or contact us for support.'
          }
          href={isStatsCalculating ? undefined : '/dashboard/console'}
          hrefTitle={isStatsCalculating ? undefined : 'Go back home'}
          footer={!isStatsCalculating}
        />
      </PageTemplate>
    );
  }

  return (
    <ErrorBoundary>
      <PageTemplate
        pages={[
          { name: 'Marketplace', href: '/dashboard/marketplace' },
          {
            name:
              !isError && headerInfoStats ? headerInfoStats?.data?.name : '--',
            href: null,
          },
        ]}
      >
        <div className="space-y-4 py-2.5">
          {isLoadingHeaderInfoStats ? (
            <HeaderLoading />
          ) : (
            headerInfoStats && <Header info={headerInfoStats.data} />
          )}
          <div className="flex lg:space-x-4">
            {isLoadingStats ? (
              <StatsLoading />
            ) : stats && stats?.mainPerformance?.totalTrades === 0 ? (
              <div className="flex justify-center items-center w-full">
                <FullPageNotification
                  height="60vh"
                  title="Oops, there is nothing to show here"
                  subTitle="This bot does not have any statistics to show. Come back in the next few days!"
                  href="/dashboard/marketplace"
                  hrefTitle="Go back to the marketplace"
                />
              </div>
            ) : (
              <>
                <div className="lg:w-2/5 xl:w-1/4 hidden lg:block">
                  <div className="bg-white-100 dark:bg-black-800 rounded-md shadow-md h-full">
                    <div className=" lg:p-4 bg-white-300 dark:bg-black-700 xxs:rounded-t-md font-normal text-lg border-bottom-new xxs:border-b-1 xxs:border-black-50 dark:border-black-400">
                      Main
                    </div>
                    <MainPerformance mainPerformance={stats?.mainPerformance} />
                  </div>
                </div>
                <Stats
                  monthlyReturns={stats?.monthlyReturns}
                  mainPerformance={stats?.mainPerformance}
                  stats={stats?.stats}
                  positions={stats?.positions}
                />
              </>
            )}
          </div>
        </div>
      </PageTemplate>
    </ErrorBoundary>
  );
};
export default MarketplacePerformancePage;
